import { ApolloError } from '@apollo/client'
import type { ApolloClient } from '@apollo/client'
import { CREATE_GUEST_SESSION } from 'api/guest-session/create-guest-session'
import { GET_GUEST_SESSION } from 'api/guest-session/get-guest-session'
import type { GraphQLFormattedError } from 'graphql'
import { guestSessionIdVar, isGuestSessionRefusedVar } from '../store'

type Client = ApolloClient<object>

/** `GuestSessionRateLimitedException` of the users service. */
const RATE_LIMIT_MESSAGE = 'Guest session create rate limit exceeded'
const TOO_MANY_REQUESTS = 429

const NOT_CREATED = { id: null, rateLimited: false }

// NOTE: production keeps only the message (`extensions.code` BAD_REQUEST);
// other stands also pass the HTTP status
const isRateLimitError = ({ message, extensions }: GraphQLFormattedError) =>
  message === RATE_LIMIT_MESSAGE || extensions?.httpStatus === TOO_MANY_REQUESTS

const isTooManyRequests = (error: unknown) => {
  const networkError = error instanceof ApolloError ? error.networkError : null

  return Boolean(
    networkError &&
    'statusCode' in networkError &&
    networkError.statusCode === TOO_MANY_REQUESTS
  )
}

const readExistingId = async (client: Client) => {
  try {
    const { data } = await client.query({
      query: GET_GUEST_SESSION,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all'
    })

    return data?.getGuestSession?.id ?? null
  } catch {
    return null
  }
}

const createId = async (client: Client) => {
  try {
    const { data, errors } = await client.mutate({
      mutation: CREATE_GUEST_SESSION,
      variables: { data: {} },
      errorPolicy: 'all'
    })

    return {
      id: data?.createGuestSession?.id ?? null,
      rateLimited: Boolean(errors?.some(isRateLimitError))
    }
  } catch (error) {
    return { ...NOT_CREATED, rateLimited: isTooManyRequests(error) }
  }
}

const resolveId = async (client: Client) => {
  const existingId = await readExistingId(client)
  const { id, rateLimited } =
    existingId ? { ...NOT_CREATED, id: existingId } : await createId(client)

  if (id) guestSessionIdVar(id)
  isGuestSessionRefusedVar(rateLimited)
  return id
}

let inFlight: Promise<string | null> | null = null

/**
 * Reads this visitor's guest session or creates one (FE-321), shared by
 * whoever asks — `GuestSessionEffect` for every anonymous visitor, the
 * Up/Down AI demo — so two callers never create two sessions.
 *
 * The landing attribution no longer goes with `createGuestSession`: it is
 * reported by `captureAttribution` once the session exists. The gateway's
 * refusal (20 sessions per IP per hour) stays silent and is not retried
 * until the next page load; after any other failure the next caller tries
 * again.
 */
export const ensureGuestSession = (client: Client): Promise<string | null> => {
  const known = guestSessionIdVar()
  if (known || isGuestSessionRefusedVar()) return Promise.resolve(known)

  inFlight ??= resolveId(client).finally(() => {
    inFlight = null
  })
  return inFlight
}
